import { useRef, useState } from "react";
import { api, API, getAccessToken, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Loader2, Upload, Download, FileText, ChevronDown, CheckCircle2,
  AlertTriangle, PlayCircle, FileCheck,
} from "lucide-react";

/**
 * Phase 33 — Admin CSV time-imports page.
 *
 * Path: /app/admin/time-imports
 *
 * Flow:
 *   1. Admin picks a file + mode (default "duration") and clicks "Dry-run".
 *      → POST /api/admin/time-imports/preview?mode=duration (multipart)
 *      → shows per-row action classification + counts.
 *   2. Admin clicks "Apply changes" to commit the create+update rows.
 *      → POST /api/admin/time-imports/commit  {batch_id}
 *   3. Admin ticks `needs_review` rows + clicks "Confirm override"
 *      → POST /api/admin/time-imports/{batch_id}/confirm-review
 *                                  {row_nums: [...]}
 *   4. "Download template" dropdown fetches /template?mode=punch|duration.
 *
 * No DB writes happen until the admin presses Apply or Confirm override —
 * preview is a pure dry-run.
 */

const ACTION_BADGE = {
  create:        { label: "Create",        cls: "bg-emerald-100 text-emerald-700" },
  update:        { label: "Update",        cls: "bg-blue-100 text-blue-700" },
  unchanged:     { label: "Unchanged",     cls: "bg-slate-100 text-slate-700" },
  needs_review:  { label: "Needs review",  cls: "bg-amber-100 text-amber-800" },
  error:         { label: "Error",         cls: "bg-rose-100 text-rose-700" },
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function TimeImportsPage() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("duration");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);  // { batch_id, counts, rows }
  const [commitResult, setCommitResult] = useState(null);
  const [reviewSelection, setReviewSelection] = useState([]);  // row_nums
  const [confirmResult, setConfirmResult] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  const resetAll = () => {
    setFile(null);
    setPreview(null);
    setCommitResult(null);
    setReviewSelection([]);
    setConfirmResult(null);
    setErrMsg("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const downloadTemplate = async (templateMode) => {
    try {
      const res = await api.get(`/admin/time-imports/template?mode=${templateMode}`, {
        responseType: "blob",
      });
      downloadBlob(
        res.data,
        `quanta-time-import-template-${templateMode}.csv`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runPreview = async () => {
    if (!file) {
      toast.error("Please pick a CSV file first");
      return;
    }
    setBusy(true);
    setErrMsg("");
    setCommitResult(null);
    setConfirmResult(null);
    setReviewSelection([]);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(
        `/admin/time-imports/preview?mode=${mode}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPreview(res.data);
      toast.success(
        `Dry-run complete — ${res.data.counts.create} create · ${res.data.counts.update} update · ${res.data.counts.needs_review} needs review · ${res.data.counts.error} error`
      );
    } catch (e) {
      setErrMsg(formatApiError(e));
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const applyBatch = async () => {
    if (!preview?.batch_id) return;
    setBusy(true);
    try {
      const res = await api.post("/admin/time-imports/commit", {
        batch_id: preview.batch_id,
      });
      setCommitResult(res.data);
      toast.success(
        `Applied — ${res.data.counts.create} created · ${res.data.counts.update} updated`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleReview = (rowNum, checked) => {
    setReviewSelection((prev) => {
      const s = new Set(prev);
      if (checked) s.add(rowNum);
      else s.delete(rowNum);
      return Array.from(s);
    });
  };

  const confirmAllReview = () => {
    const all = (preview?.rows || [])
      .filter((r) => r.action === "needs_review")
      .map((r) => r.row_num);
    setReviewSelection(all);
  };

  const applyConfirmReview = async () => {
    if (!preview?.batch_id || reviewSelection.length === 0) return;
    setBusy(true);
    try {
      const res = await api.post(
        `/admin/time-imports/${preview.batch_id}/confirm-review`,
        { row_nums: reviewSelection }
      );
      setConfirmResult(res.data);
      toast.success(
        `Confirmed — ${res.data.applied_row_nums?.length || 0} rows applied`
      );
    } catch (e) {
      // F10 — surface the batch-level ``period_locked`` 409 as a
      // readable toast instead of the generic axios message. The
      // backend returns {error:"period_locked", locked_rows:[...]}
      // when any row targets a locked week.
      const detail = e?.response?.data;
      if (detail?.error === "period_locked") {
        const n = detail.locked_rows?.length || 0;
        toast.error(
          `${n} row${n === 1 ? "" : "s"} target a locked pay-week — ` +
          "unlock the timesheet first via the correction flow. " +
          "Nothing was changed.",
          { duration: 7000 }
        );
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const needsReviewRows = (preview?.rows || []).filter((r) => r.action === "needs_review");

  return (
    <div className="space-y-6" data-testid="time-imports-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
            Administration
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">
            Time imports (Phase 33)
          </h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Upload historical time entries by CSV. Dry-run shows exactly what will
            happen; nothing writes until you press Apply. Idempotent on
            <code className="mx-1 rounded bg-muted px-1 text-xs">source_system</code>+
            <code className="rounded bg-muted px-1 text-xs">source_entry_id</code>.
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="template-dropdown">
              <Download className="h-3.5 w-3.5" /> Download template
              <ChevronDown className="ml-1 h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => downloadTemplate("duration")} data-testid="template-duration">
              <FileText className="mr-2 h-3.5 w-3.5" /> Duration (date + hours)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => downloadTemplate("punch")} data-testid="template-punch">
              <FileText className="mr-2 h-3.5 w-3.5" /> Punch (clock-in/out)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>1. Upload CSV</CardTitle>
          <CardDescription>
            The dry-run never writes to the time entries collection. Review the
            per-row action and then click Apply.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Mode</label>
              <div className="flex gap-2">
                <Button
                  variant={mode === "duration" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setMode("duration")}
                  data-testid="mode-duration"
                >
                  Duration
                </Button>
                <Button
                  variant={mode === "punch" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setMode("punch")}
                  data-testid="mode-punch"
                >
                  Punch
                </Button>
              </div>
            </div>
            <div className="space-y-1.5 flex-1 min-w-[240px]">
              <label className="text-sm font-medium">CSV file</label>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-sm file:font-medium file:text-secondary-foreground hover:file:bg-secondary/80"
                data-testid="csv-file-input"
              />
            </div>
            <Button
              onClick={runPreview}
              disabled={busy || !file}
              data-testid="run-preview-btn"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <PlayCircle className="h-4 w-4" />
              )}
              Dry-run preview
            </Button>
            {preview && (
              <Button variant="ghost" size="sm" onClick={resetAll} data-testid="reset-btn">
                Reset
              </Button>
            )}
          </div>
          {errMsg && (
            <Alert variant="destructive">
              <AlertDescription>{errMsg}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {preview && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>2. Review preview — batch {preview.batch_id.slice(0, 8)}…</CardTitle>
                <CardDescription>
                  <span className="font-mono text-xs">
                    create {preview.counts.create} · update {preview.counts.update}
                    {" · "}unchanged {preview.counts.unchanged} · needs_review{" "}
                    {preview.counts.needs_review} · error {preview.counts.error}
                  </span>
                </CardDescription>
              </div>
              <Button
                onClick={applyBatch}
                disabled={busy || !!commitResult}
                data-testid="apply-batch-btn"
              >
                {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                <FileCheck className="mr-1.5 h-4 w-4" />
                Apply {preview.counts.create + preview.counts.update} rows
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Row</TableHead>
                    <TableHead>Worker</TableHead>
                    <TableHead>Work date</TableHead>
                    <TableHead>Minutes</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((r) => {
                    const b = ACTION_BADGE[r.action] || {
                      label: r.action,
                      cls: "bg-slate-100 text-slate-700",
                    };
                    return (
                      <TableRow key={r.row_num} data-testid={`preview-row-${r.row_num}`}>
                        <TableCell className="font-mono text-xs">{r.row_num}</TableCell>
                        <TableCell>{r.worker_email || "—"}</TableCell>
                        <TableCell>{r.work_date || "—"}</TableCell>
                        <TableCell className="tabular-nums">
                          {r.duration_minutes != null ? r.duration_minutes : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {r.source_system && r.source_entry_id
                            ? `${r.source_system}:${r.source_entry_id}`
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${b.cls}`}
                            data-testid={`preview-action-${r.row_num}`}
                          >
                            {b.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {r.errors && r.errors.length > 0 ? (
                            <span className="text-rose-700">
                              {r.errors.map((e) => `${e.field}: ${e.message}`).join(" · ")}
                            </span>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {commitResult && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription data-testid="commit-result">
                Applied — {commitResult.counts.create} created,{" "}
                {commitResult.counts.update} updated. Rows marked{" "}
                <strong>needs_review</strong> were held back; confirm them
                below.
              </AlertDescription>
            </Alert>
          )}

          {needsReviewRows.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    3. Needs review ({needsReviewRows.length})
                  </CardTitle>
                  <CardDescription>
                    These rows touch an approved or locked period. Tick each one
                    you want to override, or Confirm all. Backend ensures the
                    period reopens / correction audit is written.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={confirmAllReview}
                    disabled={busy}
                    data-testid="confirm-all-btn"
                  >
                    Confirm all
                  </Button>
                  <Button
                    size="sm"
                    disabled={busy || reviewSelection.length === 0 || !!confirmResult}
                    onClick={applyConfirmReview}
                    data-testid="apply-review-btn"
                  >
                    {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                    Apply {reviewSelection.length} override{reviewSelection.length === 1 ? "" : "s"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead className="w-16">Row</TableHead>
                      <TableHead>Worker</TableHead>
                      <TableHead>Work date</TableHead>
                      <TableHead>Minutes</TableHead>
                      <TableHead>Source</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {needsReviewRows.map((r) => (
                      <TableRow key={r.row_num} data-testid={`review-row-${r.row_num}`}>
                        <TableCell>
                          <Checkbox
                            checked={reviewSelection.includes(r.row_num)}
                            onCheckedChange={(c) => toggleReview(r.row_num, c === true)}
                            data-testid={`review-check-${r.row_num}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{r.row_num}</TableCell>
                        <TableCell>{r.worker_email || "—"}</TableCell>
                        <TableCell>{r.work_date || "—"}</TableCell>
                        <TableCell className="tabular-nums">
                          {r.duration_minutes != null ? r.duration_minutes : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {r.source_system && r.source_entry_id
                            ? `${r.source_system}:${r.source_entry_id}`
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
              {confirmResult && (
                <CardContent>
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription data-testid="confirm-review-result">
                      Confirmed — {confirmResult.applied_row_nums?.length || 0}{" "}
                      row(s) applied with needs_review override.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
